// Notifications (GET /notification/:org): 12 per organization, the 4 newest
// unread, bilingual, written from the same pickups and pre-orders the pages show.

import type {
  CreateMultipleNotificationsRequest,
  CreateNotificationRequest,
  Notification,
} from '../../types/notification.type';
import {
  DAY_MS,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  DEMO_SENSORS,
  HOUR_MS,
  containersAtSite,
  facilityById,
  ids,
  iso,
  materialById,
  mondayOf,
  monthStart,
  siteById,
  type DemoPickupOrder,
} from '../core';
import { fail } from './errors';
import { getAppetite } from './organizations';
import { allPickups } from './pickups';
import { allPreOrderRecords, metricsOf, type PreOrderRecord } from './preOrders';
import { receivedKgByMaterial, targetKgForLine } from './fulfillment';

const UNREAD_COUNT = 4;

interface Draft {
  at: number;
  title_en: string;
  title_ar: string;
  message_en: string;
  message_ar: string;
  data: Record<string, unknown>;
}

const fmt = (n: number, digits = 0) => n.toLocaleString('en-US', { maximumFractionDigits: digits });

function materialNames(materialId: string): [string, string] {
  const m = materialById(materialId);
  return [m?.name_en ?? materialId, m?.name_ar ?? m?.name_en ?? materialId];
}

function siteNames(siteId: string): [string, string] {
  const s = siteById(siteId);
  return [s?.name_en ?? '', s?.name_ar ?? s?.name_en ?? ''];
}

function preOrderRef(rec: PreOrderRecord): string {
  return String(rec.data.reference ?? rec.id.slice(-6));
}

function newestFirst<T>(rows: T[], at: (row: T) => string | null): T[] {
  return rows.slice().sort((a, b) => (at(b) ?? '').localeCompare(at(a) ?? ''));
}

function recyclerDrafts(now: number): Draft[] {
  const pickups = allPickups().filter((p) => p.recycler_org_id === DEMO_RECYCLER_ORG_ID);
  const awaiting = newestFirst(
    pickups.filter((p) => p.status === 'completed' && p.approval_status === 'pending'),
    (p) => p.completed_at,
  );
  const inTransit = newestFirst(pickups.filter((p) => p.status === 'in_transit'), (p) => p.scheduled_at);
  const preOrders = allPreOrderRecords().filter((r) => r.organization_id === DEMO_RECYCLER_ORG_ID);
  const byCreated = newestFirst([...preOrders], (r) => r.created_at);
  const drafts: Draft[] = [];

  awaiting.slice(0, 2).forEach((p: DemoPickupOrder) => {
    const [en, ar] = materialNames(p.material_id);
    const [siteEn, siteAr] = siteNames(p.site_id);
    const facility = p.facility_id ? facilityById(p.facility_id) : undefined;
    drafts.push({
      at: Date.parse(p.completed_at ?? p.created_at) + 12 * 60000,
      title_en: 'Load waiting for weight approval',
      title_ar: 'حمولة بانتظار اعتماد الوزن',
      message_en: `${p.reference}: ${fmt(p.verified_kg ?? 0, 1)} kg of ${en} from ${siteEn} was weighed at ${facility?.name_en ?? 'your facility'}. Approve or reject the weight.`,
      message_ar: `${p.reference}: تم وزن ${fmt(p.verified_kg ?? 0, 1)} كجم من ${ar} من ${siteAr} في ${facility?.name_ar ?? 'منشأتكم'}. يرجى اعتماد الوزن أو رفضه.`,
      data: { type: 'pickup_order', pickup_order_id: p.id, reference: p.reference },
    });
  });

  inTransit.slice(0, 1).forEach((p) => {
    const [en, ar] = materialNames(p.material_id);
    const [siteEn, siteAr] = siteNames(p.site_id);
    drafts.push({
      at: Math.min(Date.parse(p.scheduled_at) + 20 * 60000, now - 10 * 60000),
      title_en: 'Pickup on the way to you',
      title_ar: 'شحنة في الطريق إليكم',
      message_en: `${p.reference}: about ${fmt(p.estimated_kg)} kg of ${en} left ${siteEn} and is heading to your facility.`,
      message_ar: `${p.reference}: غادرت حوالي ${fmt(p.estimated_kg)} كجم من ${ar} ${siteAr} وهي في الطريق إلى منشأتكم.`,
      data: { type: 'pickup_order', pickup_order_id: p.id, reference: p.reference, delivery_phase: 'on_way_to_recycler' },
    });
  });

  byCreated
    .filter((r) => r.status === 'pending')
    .slice(0, 2)
    .forEach((r) => {
      const [en, ar] = materialNames(r.material_id);
      const amount = `${fmt(r.amount, 2)} ${r.amount_unit}`;
      const byOps = r.origin === 'ops';
      drafts.push({
        at: Date.parse(r.created_at) + 3 * 60000,
        title_en: byOps ? 'Ops raised a pre-order for you' : 'Pre-order placed',
        title_ar: byOps ? 'أنشأ فريق العمليات طلبًا مسبقًا لكم' : 'تم إنشاء طلب مسبق',
        message_en: `${preOrderRef(r)}: ${amount} of ${en} is now open for incoming pickups.`,
        message_ar: `${preOrderRef(r)}: ${amount} من ${ar} مفتوح الآن لاستقبال الشحنات.`,
        data: { type: 'pre_order', pre_order_id: r.id, reference: preOrderRef(r) },
      });
    });

  // Weekly supply summary for last week, from the same numbers the dashboard uses.
  const lastWeekStart = mondayOf(-1).getTime();
  const lastWeekEnd = lastWeekStart + 7 * DAY_MS;
  const appetite = getAppetite();
  const received = receivedKgByMaterial(DEMO_RECYCLER_ORG_ID, lastWeekStart, lastWeekEnd, now);
  const monthDays = Math.round((monthStart(1).getTime() - monthStart(0).getTime()) / DAY_MS);
  let targetKg = 0;
  let receivedKg = 0;
  (appetite.lines ?? []).forEach((line) => {
    targetKg += targetKgForLine(line.quantity, line.unit, appetite.repeat ?? 'weekly', 'week', monthDays);
    receivedKg += received[line.material_id] ?? 0;
  });
  const pct = targetKg > 0 ? Math.round((receivedKg / targetKg) * 100) : 0;
  drafts.push({
    at: mondayOf(0).getTime() + 5 * HOUR_MS,
    title_en: 'Weekly supply summary',
    title_ar: 'ملخص التوريد الأسبوعي',
    message_en: `Last week you received ${fmt(receivedKg)} kg against a ${fmt(targetKg)} kg target (${pct}%).`,
    message_ar: `استلمتم الأسبوع الماضي ${fmt(receivedKg)} كجم مقابل مستهدف ${fmt(targetKg)} كجم (${pct}%).`,
    data: { type: 'supply_summary', week_start: iso(new Date(lastWeekStart)), percent: pct },
  });

  preOrders
    .filter((r) => r.status === 'partially_fulfilled' || r.status === 'completed')
    .forEach((r) => {
      const [en, ar] = materialNames(r.material_id);
      const m = metricsOf(r);
      const done = r.status === 'completed';
      drafts.push({
        at: Date.parse(r.updated_at) + 25 * 60000,
        title_en: done ? 'Pre-order completed' : 'Pre-order partly fulfilled',
        title_ar: done ? 'اكتمل الطلب المسبق' : 'تم تنفيذ جزء من الطلب المسبق',
        message_en: `${preOrderRef(r)} (${en}): ${fmt(m.fulfilled_kg, 1)} of ${fmt(m.amount_kg)} kg received (${Math.round(m.fulfillment_percent)}%).`,
        message_ar: `${preOrderRef(r)} (${ar}): تم استلام ${fmt(m.fulfilled_kg, 1)} من ${fmt(m.amount_kg)} كجم (${Math.round(m.fulfillment_percent)}%).`,
        data: { type: 'pre_order', pre_order_id: r.id, reference: preOrderRef(r) },
      });
    });

  preOrders
    .filter((r) => r.status === 'cancelled')
    .forEach((r) => {
      const [en, ar] = materialNames(r.material_id);
      drafts.push({
        at: Date.parse(r.updated_at),
        title_en: 'Pre-order cancelled',
        title_ar: 'تم إلغاء الطلب المسبق',
        message_en: `${preOrderRef(r)} for ${fmt(r.amount)} ${r.amount_unit} of ${en} was cancelled.`,
        message_ar: `تم إلغاء الطلب المسبق ${preOrderRef(r)} لكمية ${fmt(r.amount)} ${r.amount_unit} من ${ar}.`,
        data: { type: 'pre_order', pre_order_id: r.id, reference: preOrderRef(r) },
      });
    });

  drafts.push({
    at: monthStart(0).getTime() + 30 * HOUR_MS,
    title_en: 'Waste catalog revision 7 published',
    title_ar: 'نُشر الإصدار 7 من دليل النفايات',
    message_en: 'Copper and clear glass now carry MWAN source categories and default EWC codes. No action needed.',
    message_ar: 'أصبح للنحاس والزجاج الشفاف فئات مصدر من المركز الوطني لإدارة النفايات ورموز EWC افتراضية. لا يلزم أي إجراء.',
    data: { type: 'catalog', revision_number: 7 },
  });

  drafts.push({
    at: monthStart(0).getTime(),
    title_en: 'Monthly fulfilment report ready',
    title_ar: 'تقرير التنفيذ الشهري جاهز',
    message_en: 'Last month\'s targets-vs-received report is ready on the Dashboard.',
    message_ar: 'تقرير المستهدف مقابل المستلم للشهر الماضي جاهز في لوحة المعلومات.',
    data: { type: 'report', month_start: iso(monthStart(-1)) },
  });

  // Older history, so the newest-12 window is always full whatever the clock says
  // (some of the drafts above fall in the future right after a week or month starts).
  newestFirst(
    pickups.filter((p) => p.status === 'completed' && p.approval_status === 'approved'),
    (p) => p.completed_at,
  )
    .slice(0, 12)
    .forEach((p) => {
      const [en, ar] = materialNames(p.material_id);
      drafts.push({
        at: Date.parse(p.completed_at ?? p.created_at) + 3 * HOUR_MS,
        title_en: 'Weight approved',
        title_ar: 'تم اعتماد الوزن',
        message_en: `${p.reference}: you approved ${fmt(p.verified_kg ?? 0, 1)} kg of ${en}. Payable SAR ${fmt(p.sale_amount_sar ?? 0, 2)}.`,
        message_ar: `${p.reference}: اعتمدتم ${fmt(p.verified_kg ?? 0, 1)} كجم من ${ar}. المبلغ المستحق ${fmt(p.sale_amount_sar ?? 0, 2)} ريال.`,
        data: { type: 'pickup_order', pickup_order_id: p.id, reference: p.reference },
      });
    });

  return drafts;
}

function outletDrafts(now: number): Draft[] {
  const pickups = allPickups().filter((p) => p.outlet_org_id === DEMO_OUTLET_ORG_ID);
  const drafts: Draft[] = [];
  const approved = newestFirst(pickups.filter((p) => p.approval_status === 'approved'), (p) => p.completed_at);
  // The newest three lead; older approvals pad the list so it always holds 12.
  approved.slice(0, 12).forEach((p) => {
    const [en, ar] = materialNames(p.material_id);
    drafts.push({
      at: Date.parse(p.completed_at ?? p.created_at) + 5 * HOUR_MS,
      title_en: 'Weight approved',
      title_ar: 'تم اعتماد الوزن',
      message_en: `${p.reference}: ${fmt(p.verified_kg ?? 0, 1)} kg of ${en} approved. Sale amount SAR ${fmt(p.sale_amount_sar ?? 0, 2)}.`,
      message_ar: `${p.reference}: تم اعتماد ${fmt(p.verified_kg ?? 0, 1)} كجم من ${ar}. قيمة البيع ${fmt(p.sale_amount_sar ?? 0, 2)} ريال.`,
      data: { type: 'pickup_order', pickup_order_id: p.id, reference: p.reference },
    });
  });
  const rejected = newestFirst(pickups.filter((p) => p.approval_status === 'rejected'), (p) => p.completed_at);
  rejected.slice(0, 1).forEach((p) => {
    const [en, ar] = materialNames(p.material_id);
    drafts.push({
      at: Date.parse(p.completed_at ?? p.created_at) + 6 * HOUR_MS,
      title_en: 'Weight rejected by the recycler',
      title_ar: 'رفض المُعيد تدويره الوزن',
      message_en: `${p.reference}: the ${en} load was rejected for contamination. No sale was recorded.`,
      message_ar: `${p.reference}: تم رفض حمولة ${ar} بسبب التلوث. لم تُسجل عملية بيع.`,
      data: { type: 'pickup_order', pickup_order_id: p.id, reference: p.reference },
    });
  });
  const transit = newestFirst(pickups.filter((p) => p.status === 'in_transit'), (p) => p.scheduled_at);
  transit.slice(0, 3).forEach((p) => {
    const [en, ar] = materialNames(p.material_id);
    const [siteEn, siteAr] = siteNames(p.site_id);
    drafts.push({
      at: Math.min(Date.parse(p.scheduled_at) + 15 * 60000, now - 5 * 60000),
      title_en: 'Pickup collected',
      title_ar: 'تم استلام الشحنة',
      message_en: `${p.reference}: ${en} collected from ${siteEn}.`,
      message_ar: `${p.reference}: تم استلام ${ar} من ${siteAr}.`,
      data: { type: 'pickup_order', pickup_order_id: p.id, reference: p.reference },
    });
  });
  const pending = newestFirst(pickups.filter((p) => p.status === 'pending'), (p) => p.created_at);
  pending.slice(0, 3).forEach((p) => {
    const [en, ar] = materialNames(p.material_id);
    const [siteEn, siteAr] = siteNames(p.site_id);
    drafts.push({
      at: Date.parse(p.created_at) + 2 * 60000,
      title_en: 'Pickup requested',
      title_ar: 'تم طلب استلام',
      message_en: `${p.reference}: ${fmt(p.estimated_kg)} kg of ${en} at ${siteEn} (container ${p.fill_level}% full).`,
      message_ar: `${p.reference}: ${fmt(p.estimated_kg)} كجم من ${ar} في ${siteAr} (الحاوية ممتلئة ${p.fill_level}%).`,
      data: { type: 'pickup_order', pickup_order_id: p.id, reference: p.reference },
    });
  });
  const lowBattery = DEMO_SENSORS.find((s) => s.status === 'low_battery');
  if (lowBattery) {
    const [siteEn, siteAr] = siteNames(lowBattery.site_id);
    drafts.push({
      at: now - 3 * DAY_MS - 4 * HOUR_MS,
      title_en: 'Sensor battery low',
      title_ar: 'بطارية المستشعر منخفضة',
      message_en: `Fill sensor ${lowBattery.serial_number} at ${siteEn} is at ${lowBattery.battery_pct}% battery.`,
      message_ar: `بطارية مستشعر التعبئة ${lowBattery.serial_number} في ${siteAr} عند ${lowBattery.battery_pct}%.`,
      data: { type: 'sensor', sensor_id: lowBattery.id },
    });
  }
  const fullest = containersAtSite(ids.site(1)).slice().sort((a, b) => b.fill_level - a.fill_level)[0];
  if (fullest) {
    const [en, ar] = materialNames(fullest.material_id);
    drafts.push({
      at: now - 5 * DAY_MS - 2 * HOUR_MS,
      title_en: 'Container almost full',
      title_ar: 'الحاوية شبه ممتلئة',
      message_en: `${fullest.code} (${en}) at Olaya Flagship reached ${fullest.fill_level}%.`,
      message_ar: `وصلت الحاوية ${fullest.code} (${ar}) في فرع العليا الرئيسي إلى ${fullest.fill_level}%.`,
      data: { type: 'container', container_id: fullest.id },
    });
  }
  return drafts;
}

function finalize(orgId: string, drafts: Draft[], now: number): Notification[] {
  return drafts
    .filter((d) => Number.isFinite(d.at) && d.at <= now)
    .sort((a, b) => b.at - a.at)
    .slice(0, 12)
    .map((d, i) => ({
      id: ids.notification((orgId === DEMO_RECYCLER_ORG_ID ? 100 : 200) + i + 1),
      title_en: d.title_en,
      title_ar: d.title_ar,
      message_en: d.message_en,
      message_ar: d.message_ar,
      type: 'in_app' as const,
      data: { ...d.data, organization_id: orgId },
      is_read: i >= UNREAD_COUNT,
      created_at: iso(new Date(d.at)),
    }));
}

let byOrg: Record<string, Notification[]> = {};
let createdCount = 0;

export function resetNotifications(now = Date.now()): void {
  byOrg = {
    [DEMO_RECYCLER_ORG_ID]: finalize(DEMO_RECYCLER_ORG_ID, recyclerDrafts(now), now),
    [DEMO_OUTLET_ORG_ID]: finalize(DEMO_OUTLET_ORG_ID, outletDrafts(now), now),
  };
  createdCount = 0;
}

export function listNotifications(
  orgId: string,
  q: { limit?: unknown; before_message_id?: unknown; after_message_id?: unknown },
): { result_list: Notification[]; total_count: number } {
  const all = byOrg[orgId] ?? [];
  let rows = all;
  const before = typeof q.before_message_id === 'string' ? all.findIndex((n) => n.id === q.before_message_id) : -1;
  const after = typeof q.after_message_id === 'string' ? all.findIndex((n) => n.id === q.after_message_id) : -1;
  if (before >= 0) rows = all.slice(before + 1);
  else if (after >= 0) rows = all.slice(0, after);
  const limit = Number.parseInt(String(q.limit ?? ''), 10);
  if (Number.isFinite(limit) && limit > 0) rows = rows.slice(0, limit);
  return { result_list: rows, total_count: all.length };
}

export function unreadCount(orgId: string): number {
  return (byOrg[orgId] ?? []).filter((n) => !n.is_read).length;
}

export function markNotificationRead(orgId: string, notificationId: string): Notification {
  const current = (byOrg[orgId] ?? []).find((n) => n.id === notificationId);
  if (!current) fail(404, 'Notification not found');
  const next: Notification = { ...current, is_read: true };
  byOrg = { ...byOrg, [orgId]: (byOrg[orgId] ?? []).map((n) => (n.id === notificationId ? next : n)) };
  return next;
}

function addNotification(orgId: string, req: CreateNotificationRequest | CreateMultipleNotificationsRequest): Notification {
  createdCount += 1;
  const row: Notification = {
    id: ids.notification(900 + createdCount),
    title_en: req.title_en,
    title_ar: req.title_ar,
    message_en: req.message_en,
    message_ar: req.message_ar,
    type: req.type ?? 'in_app',
    data: { ...(req.data ?? {}), organization_id: orgId },
    is_read: false,
    created_at: iso(new Date()),
  };
  byOrg = { ...byOrg, [orgId]: [row, ...(byOrg[orgId] ?? [])] };
  return row;
}

export function createNotification(req: CreateNotificationRequest): Notification {
  return addNotification(req.organization_id, req);
}

export function createNotifications(req: CreateMultipleNotificationsRequest): Notification[] {
  return (req.user_ids ?? []).map(() => addNotification(req.organization_id, req));
}
